import React, { useEffect, useState } from 'react';
import {
  AddTeamRequest,
  InvitedTeamResponse,
  TeamResponse
} from '../models/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { list, add, update, remove } from '../services/team.service';
import {
  list as listInvites,
  confirm as confirmInvite,
  remove as removeInvite
} from '../services/teamInvite.service';
import DateRender from '../components/DateRender';
import ModalForm from '../components/Modal';
import TeamsForm from '../components/TeamsForm';
import DeleteButton from '../components/DeleteButton';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import Feedback from '../components/Feedback';

const Teams: React.FC = () => {
  const [teams, setTeams] = useState<TeamResponse[]>([]);
  const [loading, setLoading] = useState<boolean | null>();
  const [errors, setErrors] = useState([] as string[]);
  const [loadMessage, setLoadMessage] = useState('');
  const [isAddingTeam, setIsAddingTeam] = useState(false);
  const [isAddingTeamLoading, setIsAddingTeamLoading] = useState(false);
  const [addErrors, setAddErrors] = useState([] as string[]);
  const [editingTeam, setEditingTeam] = useState<TeamResponse | null>(null);
  const [teamDeletingReferences, setTeamDeletingReferences] = useState(
    [] as string[]
  );

  const [teamInvites, setTeamInvites] = useState<InvitedTeamResponse[]>([]);
  const [teamInviteDeletingReferences, setTeamInviteDeletingReferences] =
    useState([] as string[]);
  const [loadingInvites, setLoadingInvites] = useState<boolean | null>();
  const [inviteErrors, setInviteErrors] = useState([] as string[]);
  const [teamInviteJoinReferences, setTeamInviteJoinReferences] = useState(
    [] as string[]
  );

  const getTeams = async () => {
    if (loading) {
      return;
    }
    setLoading(true);
    try {
      const teamsResponse = await list();
      if (teamsResponse.success) {
        setTeams(teamsResponse.data?.teams || []);
        setErrors([]);
      } else {
        setLoadMessage('');
        setErrors(teamsResponse.errors);
      }
    } catch (error) {
      setErrors(['There was a problem loading the teams']);
    } finally {
      setLoading(false);
    }
  };
  const getInvites = async () => {
    if (loadingInvites) {
      return;
    }
    setLoadingInvites(true);
    try {
      const teamsResponse = await listInvites();
      if (teamsResponse.success) {
        setTeamInvites(teamsResponse.data?.teams || []);
        setInviteErrors([]);
      } else {
        setInviteErrors(teamsResponse.errors);
      }
    } catch (error) {
      setInviteErrors(['There was a problem loading the teams']);
    } finally {
      setLoadingInvites(false);
    }
  };

  useEffect(() => {
    getTeams().catch(console.error);
    getInvites().catch(console.error);
  }, []);

  const submit = async (request: AddTeamRequest) => {
    setIsAddingTeamLoading(true);

    try {
      if (editingTeam) {
        const result = await update(editingTeam.reference, request);
        if (result.success && result.data) {
          setLoadMessage('Team updated');
          setIsAddingTeam(false);
          setEditingTeam(null);
          setAddErrors([]);
          await getTeams();
          return true;
        } else {
          setAddErrors(result.errors);
          return false;
        }
      } else {
        const result = await add(request);
        if (result.success && result.data) {
          setLoadMessage('Team added');
          setIsAddingTeam(false);
          setAddErrors([]);
          await getTeams();
          return true;
        } else {
          setAddErrors(result.errors);
          return false;
        }
      }
    } finally {
      setIsAddingTeamLoading(false);
    }
  };

  const handleDelete = async (team: TeamResponse) => {
    if (teamDeletingReferences.includes(team.reference)) {
      return;
    }
    setTeamDeletingReferences((p) => [...p, team.reference]);

    const result = await remove(team.reference);
    if (result.success) {
      const teamsResponse = await list();
      if (teamsResponse.success) {
        setTeams(teamsResponse.data?.teams || []);
        setLoadMessage('Team deleted');
        setAddErrors([]);
      } else {
        setAddErrors(teamsResponse.errors);
      }
    } else {
      setLoadMessage('');
      setAddErrors(['There was a problem']);
    }
    setTeamDeletingReferences((p) => p.filter((q) => q !== team.reference));
  };

  const handleDeleteInvite = async (team: InvitedTeamResponse) => {
    if (teamInviteDeletingReferences.includes(team.reference)) {
      return;
    }
    setTeamInviteDeletingReferences((p) => [...p, team.reference]);

    const result = await removeInvite(team.reference);
    if (result.success) {
      const teamsResponse = await listInvites();
      if (teamsResponse.success) {
        setTeamInvites(teamsResponse.data?.teams || []);
        setLoadMessage('Invite deleted');
        setInviteErrors([]);
      } else {
        setInviteErrors(teamsResponse.errors);
      }
    } else {
      setLoadMessage('');
      setInviteErrors(['There was a problem']);
    }
    setTeamInviteDeletingReferences((p) =>
      p.filter((q) => q !== team.reference)
    );
  };

  const handleConfirmInvite = async (team: InvitedTeamResponse) => {
    if (teamInviteJoinReferences.includes(team.reference)) {
      return;
    }
    setTeamInviteJoinReferences((p) => [...p, team.reference]);

    const result = await confirmInvite(team.reference);
    if (result.success) {
      const teamsResponse = await listInvites();
      if (teamsResponse.success) {
        setTeamInvites(teamsResponse.data?.teams || []);
        setLoadMessage('Invite accepted');
        setInviteErrors([]);
        await getInvites();
        await getTeams();
      } else {
        setInviteErrors(teamsResponse.errors);
      }
    } else {
      setLoadMessage('');
      setInviteErrors(['There was a problem']);
    }
    setTeamInviteJoinReferences((p) => p.filter((q) => q !== team.reference));
  };

  return (
    <>
      {loading && <LoadingSpinner />}
      {loading === false && (
        <div className="content-body">
          <h2>Your teams</h2>
          <Feedback message={loadMessage} type="success" />
          <Feedback messages={errors} type="error" />
          <button type="button" onClick={() => setIsAddingTeam(true)}>
            Add Team
          </button>
          {isAddingTeam && (
            <ModalForm>
              <TeamsForm
                errors={addErrors}
                isSubmitting={isAddingTeamLoading}
                model={editingTeam}
                submit={submit}
                close={() => {
                  setIsAddingTeam(false);
                  setEditingTeam(null);
                  setAddErrors([]);
                }}
                buttonText={editingTeam ? 'Update Team' : 'Add Team'}
              />
            </ModalForm>
          )}
          {teams.length === 0 && <p>No teams found</p>}
          {teams.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Is Owner</th>
                  <th>Members</th>
                  <th>Created Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {teams.map((item) => (
                  <tr key={item.reference}>
                    <td>{item.name}</td>
                    <td>{item.isOwner ? 'Yes' : 'No'}</td>
                    <td>{item.totalMembers}</td>
                    <td>
                      <DateRender dateString={item.createdDateUtc} />
                    </td>
                    <td>
                      {item.isOwner && (
                        <>
                          <Link to={`/teams/${item.reference}`}>View</Link>
                          <button
                            onClick={() => {
                              setEditingTeam(item);
                              setIsAddingTeam(true);
                            }}
                          >
                            Edit
                          </button>
                          <DeleteButton
                            isLoading={teamDeletingReferences.includes(
                              item.reference
                            )}
                            onClick={() => handleDelete(item)}
                          >
                            Delete
                          </DeleteButton>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {teamInvites.length > 0 && (
            <>
              <h2>Your invites</h2>
              <Feedback messages={inviteErrors} type="error" />

              <table>
                <thead>
                  <tr>
                    <th>Team</th>
                    <th>Invite Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {teamInvites.map((item) => (
                    <tr key={item.reference}>
                      <td>{item.name}</td>
                      <td>
                        <DateRender dateString={item.inviteDateUtc} />
                      </td>
                      <td>
                        <Button
                          isLoading={teamInviteJoinReferences.includes(
                            item.reference
                          )}
                          onClick={() => handleConfirmInvite(item)}
                        >
                          Join
                        </Button>
                        <DeleteButton
                          isLoading={teamInviteDeletingReferences.includes(
                            item.reference
                          )}
                          onClick={() => handleDeleteInvite(item)}
                        >
                          Delete
                        </DeleteButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default Teams;
